import { onSnapshot, Unsubscribe, orderBy, query, limit, where, startAfter, getDocs, DocumentSnapshot } from 'firebase/firestore';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import { Paths } from './firestoreUtils';

// Hot control-plane doc listener (general config) — path: users/{uid}/user_general/user_general
export function onUserGeneralSnapshot(
  uid: string,
  handler: (data: any | null) => void,
  onError?: (e: any) => void
): Unsubscribe {
  // Reads only; guard not required for listener, but ensure we never accidentally write under wrong uid
  try { addUserScopedWriteGuard(uid, 'onUserGeneralSnapshot:read'); } catch {}
  const ref = Paths.userConfigGeneral(uid);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) return handler(null);
    const data = snap.data() as any;
    const arr: any[] = Array.isArray(data?.user_general) ? data.user_general : [];
    handler(arr.length > 0 ? arr[0] : {});
  }, onError);
}

// Active conversation messages live listener (paginate window size)
export function onConversationMessagesSnapshot(
  uid: string,
  conversationId: string,
  pageSize: number,
  handler: (items: any[]) => void,
  onError?: (e: any) => void
): Unsubscribe {
  const col = Paths.conversationMessagesCol(uid, conversationId);
  const qy = query(col, orderBy('ts', 'asc'), limit(pageSize));
  return onSnapshot(qy, (snap) => handler(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }))), onError);
}

// Platform message history: live window and pagination helpers
export function onPlatformMessagesSnapshot(
  uid: string,
  opts: { type?: string; priority?: string; startIso?: string; endIso?: string; limitSize?: number },
  handler: (items: any[]) => void,
  onError?: (e: any) => void
): Unsubscribe {
  const col = Paths.userPlatformMessagesCol(uid);
  const parts: any[] = [orderBy('created_at', 'desc')];
  if (opts?.type) parts.push(where('message_type', '==', String(opts.type)));
  if (opts?.priority) parts.push(where('priority_level', '==', String(opts.priority)));
  if (opts?.startIso) parts.push(where('created_at', '>=', (window as any).firebase?.firestore?.Timestamp?.fromDate(new Date(opts.startIso)) || null));
  if (opts?.endIso) parts.push(where('created_at', '<=', (window as any).firebase?.firestore?.Timestamp?.fromDate(new Date(opts.endIso)) || null));
  if (opts?.limitSize) parts.push(limit(Math.max(1, Math.min(100, opts.limitSize))));
  const qy = query(col, ...parts);
  return onSnapshot(qy, (snap) => handler(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }))), onError);
}

// PRE-70: Session-scoped platform message listener — only returns messages tagged with the given session_id.
// Used by LandingPage to isolate AI responses per workflow thread.
export function onSessionPlatformMessagesSnapshot(
  uid: string,
  sessionId: string,
  handler: (items: any[]) => void,
  onError?: (e: any) => void
): Unsubscribe {
  const col = Paths.userPlatformMessagesCol(uid);
  const qy = query(
    col,
    where('metadata.session_id', '==', sessionId),
    orderBy('created_at', 'asc')
  );
  return onSnapshot(qy, (snap) => handler(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }))), onError);
}

export async function paginatePlatformMessages(
  uid: string,
  cursor: DocumentSnapshot | null,
  opts?: { type?: string; priority?: string; pageSize?: number }
): Promise<{ items: any[]; next: DocumentSnapshot | null }> {
  const col = Paths.userPlatformMessagesCol(uid);
  const parts: any[] = [orderBy('created_at', 'desc')];
  if (opts?.type) parts.push(where('message_type', '==', String(opts.type)));
  if (opts?.priority) parts.push(where('priority_level', '==', String(opts.priority)));
  parts.push(limit(Math.max(1, Math.min(50, opts?.pageSize || 20))));
  let qy = query(col, ...parts);
  if (cursor) qy = query(qy, startAfter(cursor));
  const snap = await getDocs(qy);
  const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
  const next = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  return { items, next };
}


